import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Card,
  CardHeader,
  Spinner,
  Button,
  Input,
  FormGroup,
  Label,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
  Form,
} from 'reactstrap';
import Header from '../../components/Headers/Header';
import CustomTable from '../../components/CustomTable';
import LeaveRequestModal from '../../components/Modals/LeaveRequest';
import ViewLeaveRequestModal from '../../components/Modals/ViewLeaveRequest';
import axios from 'axios';
import { apiURL1 } from '../../config';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from 'components/Footers/Footer';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaTimes } from 'react-icons/fa';

const LeaveManagement = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [view, setView] = useState('staffPending');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [addLeaveModal, setAddLeaveModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [currentLeave, setCurrentLeave] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [newLeave, setNewLeave] = useState({
    userId: null,
    leaveType: null,
    fromDate: null,
    toDate: null,
    leaveReason: '',
  });
  const itemsPerPage = 20; // Matches backend limit
  const approveToggle = () => setApproveModal(!approveModal);
  const toggleViewModal = () => setViewModalOpen(!viewModalOpen);
  const toggleAddLeaveModal = () => setAddLeaveModal(!addLeaveModal);

  const [filter, setFilter] = useState({
    date: '',
    reason: '',
    status: '',
    student: '',
    className: '',
    type: '',
  });

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleFilterChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  const handleViewChange = (newView) => {
    const targetView = newView === 'staff' ? 'staffPending' : newView === 'student' ? 'studentPending' : newView;
    setView(targetView);
    setFilter({
      date: '',
      reason: '',
      status: '',
      student: '',
      className: '',
      type: '',
    });
    setCurrentPage(1);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let url;
      const isStaff = view.startsWith('staff');
      const isPending = view.endsWith('Pending');
      if (isStaff) {
        url = isPending
          ? `${apiURL1}/superadmin/leave/requests/teachers`
          : `${apiURL1}/superadmin/leave/history/teachers`;
      } else {
        url = isPending
          ? `${apiURL1}/superadmin/leave/requests/students`
          : `${apiURL1}/superadmin/leave/history/students`;
      }
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { page: currentPage, limit: itemsPerPage },
      });
      console.log('API Response for', view, ':', response.data);
      let leaveData = [];
      if (isStaff) {
        const dataSource = Array.isArray(response.data) ? response.data : response.data.teachers || [];
        leaveData = dataSource.flatMap((teacher) =>
          (teacher.leaves || []).map((leave) => ({
            ...leave,
            teacherId: teacher._id,
            leaveId: leave._id,
            name: teacher.name,
            email: teacher.email,
          })),
        );
        setColumns([
          'Staff Name',
          'Reason',
          'Type',
          'Days',
          'Status',
          'From Date',
          'To Date',
          'Action',
        ]);
      } else {
        const dataSource = Array.isArray(response.data) ? response.data : response.data.students || [];
        leaveData = dataSource.flatMap((student) =>
          (student.leaves || []).map((leave) => ({
            ...leave,
            studentId: student._id || student.id,
            leaveId: leave._id,
            name: student.fullName || student.name,
            class: student.class,
            email: student.email,
          })),
        );
        setColumns([
          'Student Name',
          'Reason',
          'Type',
          'Days',
          'Status',
          'From Date',
          'To Date',
          'Class',
          'Action',
        ]);
      }
      console.log('leaveData before filtering for', view, ':', leaveData);
      if (isPending) {
        leaveData = leaveData.filter((leave) =>
          leave.status && leave.status.toLowerCase() === 'pending'
        );
      }
      console.log('Filtered leaveData for', view, ':', leaveData);
      leaveData.sort((a, b) => {
        const dateA = new Date(a.fromDate);
        const dateB = new Date(b.fromDate);
        if (dateB > dateA) return 1;
        if (dateA > dateB) return -1;
        const dateA2 = new Date(a.toDate);
        const dateB2 = new Date(b.toDate);
        if (dateB2 > dateA2) return 1;
        if (dateA2 > dateB2) return -1;
        return 0;
      });
      setRows(leaveData);
      setTotalPages(response.data.total ? Math.ceil(response.data.total / itemsPerPage) : 1);
    } catch (error) {
      console.error('Error fetching data:', error);
      setRows([]);
      toast.error('Error fetching leave requests!');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${apiURL1}/superadmin/getTeachersName`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { limit: 1000 },
      });
      setTeachers(
        response.data.teachers?.map((teacher) => ({
          value: teacher._id,
          label: teacher.name || 'Unknown',
        })) || []
      );
      if (!response.data.teachers?.length) {
        toast.warn('No teachers found.');
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error(error.response?.data?.message || 'Error fetching teachers!');
      setTeachers([]);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${apiURL1}/superadmin/getStudentsName`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { limit: 1000 },
      });
      setStudents(
        response.data.students?.map((student) => ({
          value: student._id || student.id,
          label: student.fullName || student.name || 'Unknown',
        })) || []
      );
      if (!response.data.students?.length) {
        toast.warn('No students found.');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(error.response?.data?.message || 'Error fetching students!');
      setStudents([]);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await axios.get(`${apiURL1}/superadmin/leave/types`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setLeaveTypes(
        response.data.map((type) => ({
          value: type._id,
          label: type.name,
        }))
      );
    } catch (error) {
      console.error('Error fetching leave types:', error);
      toast.error('Error fetching leave types!');
      setLeaveTypes([]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTeachers();
    fetchStudents();
    fetchLeaveTypes();
  }, [view, currentPage]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const handleRemoveLeave = async (leave) => {
    try {
      const url = view.startsWith('staff')
        ? `${apiURL1}/superadmin/leave/requests/teachers/${leave.teacherId}/${leave.leaveId}`
        : `${apiURL1}/superadmin/leave/requests/student/${leave.studentId}/${leave.leaveId}`;
      await axios.delete(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchData();
      toast.success('Leave request removed successfully!');
    } catch (error) {
      console.error('Error removing leave request:', error);
      toast.error(error.response?.data?.message || 'Error removing leave request!');
    }
  };

  const reArrageData = (items) => {
    const arrangedData = items ? items.map((item) => {
      const fromDate = new Date(item.fromDate);
      const toDate = new Date(item.toDate);
      const timeDiff = Math.abs(toDate.getTime() - fromDate.getTime());
      const days = Math.ceil(timeDiff / (1000 * 3600 * 24));
      const baseData = {
        name: truncateText(item.name, 20),
        reason: truncateText(item.leaveReason, 20),
        type: truncateText(item.leaveType, 15),
        days: days + 1,
        status: truncateText(item.status, 10),
        fromDate: fromDate.toLocaleDateString(),
        toDate: toDate.toLocaleDateString(),
      };
      if (view.startsWith('staff')) {
        return {
          ...baseData,
          actions: (
            <div className="d-flex">
              <Button
                size="sm"
                color="info"
                onClick={() => {
                  setCurrentLeave({ ...item }); // Include all item fields
                  toggleViewModal();
                }}
              >
                <i className="fa-solid fa-eye"></i>
              </Button>
              {view === 'staffPending' && (
                <Button
                  size="sm"
                  color="success"
                  onClick={() => {
                    setApproveModal(true);
                    setSelectedLeave({ ...item }); // Include all item fields
                  }}
                >
                  <i className="fa-solid fa-check"></i>
                </Button>
              )}
              <Button
                size="sm"
                color="primary"
                onClick={() => navigate(`/admin/AddTeacher/${item.teacherId}`)}
              >
                <i className="fa-solid fa-arrow-right"></i>
              </Button>
            </div>
          ),
        };
      } else {
        return {
          ...baseData,
          class: truncateText(item.class, 10),
          actions: (
            <div className="d-flex">
              <Button
                size="sm"
                color="info"
                onClick={() => {
                  setCurrentLeave({ ...item }); // Include all item fields
                  toggleViewModal();
                }}
              >
                <i className="fa-solid fa-eye"></i>
              </Button>
              {view === 'studentPending' && (
                <Button
                  size="sm"
                  color="success"
                  onClick={() => {
                    setApproveModal(true);
                    setSelectedLeave({ ...item }); // Include all item fields
                  }}
                >
                  <i className="fa-solid fa-check"></i>
                </Button>
              )}
              <Button
                size="sm"
                color="primary"
                onClick={() => navigate(`/admin/student-details/${item.studentId}`)}
              >
                <i className="fa-solid fa-arrow-right"></i>
              </Button>
            </div>
          ),
        };
      }
    }) : [];
    console.log('Arranged data for rendering:', arrangedData);
    return arrangedData;
  };

  const filteredData = useMemo(() => {
    if (!rows) return [];
    return reArrageData(
      rows.filter((item) => {
        const fromDate = new Date(item.fromDate);
        const filterDate = filter.date ? new Date(filter.date) : null;
        return (
          (filter.date === '' ||
            (filterDate &&
              fromDate.getFullYear() === filterDate.getFullYear() &&
              fromDate.getMonth() === filterDate.getMonth() &&
              fromDate.getDate() === filterDate.getDate())) &&
          (filter.reason === '' ||
            item.leaveReason?.toLowerCase().includes(filter.reason?.toLowerCase())) &&
          (filter.status === '' ||
            item.status?.toLowerCase().includes(filter.status?.toLowerCase())) &&
          (filter.student === '' ||
            item.name?.toLowerCase().includes(filter.student?.toLowerCase())) &&
          (filter.className === '' ||
            item.class?.toLowerCase().includes(filter.className?.toLowerCase())) &&
          (filter.type === '' ||
            item.leaveType?.toLowerCase().includes(filter.type?.toLowerCase()))
        );
      }),
    );
  }, [rows, filter, view]);

  const handleApproveReject = async (status) => {
    if (!selectedLeave) return;
    console.log('Selected leave for approve/reject:', selectedLeave);
    try {
      let url = view.startsWith('staff')
        ? `${apiURL1}/superadmin/leave/requests/teachers/${selectedLeave.teacherId}/${selectedLeave.leaveId}`
        : `${apiURL1}/superadmin/leave/requests/student/${selectedLeave.studentId || selectedLeave.id}/${selectedLeave.leaveId}`;
      await axios.put(url, { status }, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchData();
      approveToggle();
      toast.success(`Leave ${status} successfully!`);
    } catch (error) {
      console.error('Error updating leave status:', error);
      toast.error(error.response?.data?.message || 'Error updating leave status!');
    }
  };

  const handleNewLeaveChange = (field, value) => {
    setNewLeave({ ...newLeave, [field]: value });
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { userId, leaveType, fromDate, toDate, leaveReason } = newLeave;
      if (!userId || !leaveType || !fromDate || !toDate || !leaveReason) {
        toast.error('Please fill all required fields.');
        return;
      }
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      if (new Date(formattedToDate) < new Date(formattedFromDate)) {
        toast.error('To Date cannot be before From Date.');
        return;
      }
      const url = view.startsWith('staff')
        ? `${apiURL1}/superadmin/addleave/requests/teacher`
        : `${apiURL1}/superadmin/addleave/requests/student`;
      await axios.post(url, {
        id: userId.value,
        leaveType: leaveType.value,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        leaveReason,
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setCurrentPage(1);
      setView(view.startsWith('staff') ? 'staffPending' : 'studentPending');
      fetchData();
      toggleAddLeaveModal();
      setNewLeave({ userId: null, leaveType: null, fromDate: null, toDate: null, leaveReason: '' });
      toast.success('Leave request submitted successfully!');
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error(error.response?.data?.message || 'Error submitting leave request!');
    } finally {
      setLoading(false);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 px-0 px-md-3 d-flex align-items-center justify-content-between">
                <div>
                  <h3 className="mb-0">Leave Management</h3>
                  <Button
                    color={view.startsWith('staff') ? 'primary' : 'secondary'}
                    onClick={() => handleViewChange('staff')}
                  >
                    Staff
                  </Button>
                  <Button
                    color={view.startsWith('student') ? 'primary' : 'secondary'}
                    onClick={() => handleViewChange('student')}
                  >
                    Student
                  </Button>
                  <Button
                    color="success"
                    onClick={toggleAddLeaveModal}
                  >
                    Add Leave
                  </Button>
                </div>
              </CardHeader>
              {(view.startsWith('staff') || view.startsWith('student')) && (
                <div className="p-3 border-bottom">
                  <Row>
                    <Col>
                      <Button
                        color={view.endsWith('Pending') ? 'primary' : 'secondary'}
                        onClick={() => handleViewChange(view.startsWith('staff') ? 'staffPending' : 'studentPending')}
                      >
                        Pending
                      </Button>
                      <Button
                        color={view.endsWith('All') ? 'primary' : 'secondary'}
                        onClick={() => handleViewChange(view.startsWith('staff') ? 'staffAll' : 'studentAll')}
                      >
                        All
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}
              <div className="p-3">
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="student">
                        {view.startsWith('staff') ? 'Staff' : 'Student'}
                      </Label>
                      <Input
                        type="text"
                        name="student"
                        id="student"
                        placeholder={`Enter ${view.startsWith('staff') ? 'staff' : 'student'} name`}
                        value={filter.student}
                        onChange={handleFilterChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="date">Date</Label>
                      <Input
                        type="date"
                        name="date"
                        id="date"
                        value={filter.date}
                        onChange={handleFilterChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="type">Type</Label>
                      <Input
                        type="select"
                        name="type"
                        id="type"
                        value={filter.type}
                        onChange={handleFilterChange}
                      >
                        <option value="">All</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  {view.startsWith('student') && (
                    <Col md={3}>
                      <FormGroup>
                        <Label for="className">Class</Label>
                        <Input
                          type="text"
                          name="className"
                          id="className"
                          placeholder="Enter class name"
                          value={filter.className}
                          onChange={handleFilterChange}
                        />
                      </FormGroup>
                    </Col>
                  )}
                </Row>
              </div>
              {loading ? (
                <Spinner size="lg" className="my-5 mx-auto" />
              ) : (
                <>
                  <CustomTable columns={columns} rows={currentItems} />
                  <Pagination className="d-flex justify-content-center">
                    {pageNumbers.map((number) => (
                      <PaginationItem key={number} active={number === currentPage}>
                        <PaginationLink onClick={() => paginate(number)}>
                          {number}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                  </Pagination>
                </>
              )}
            </Card>
          </div>
        </Row>
        <Footer />
      </Container>

      <Modal isOpen={approveModal} toggle={approveToggle}>
        <ModalHeader className="pb-0">Approve/Reject Leave Request</ModalHeader>
        <ModalBody className="pb-0 d-flex flex-column justify-content-center align-items-center">
          <Row>
            <i
              className="fa fa-times-circle fa-3x fa-primary"
              style={{ color: '#D7B665' }}
              aria-hidden="true"
            ></i>
          </Row>
          <Row className="mt-4">
            Are you sure about Approve {view.startsWith('staff') ? 'Staff' : 'Student'} Leave Request?
          </Row>
          <Button
            color="success"
            onClick={() => handleApproveReject('approved')}
            className="w-100 mt-4"
          >
            Yes, Approve
          </Button>
          <Button
            color="danger"
            onClick={() => handleApproveReject('rejected')}
            className="w-100 mt-4"
          >
            Yes, Reject
          </Button>
          <Button
            color="secondary"
            onClick={approveToggle}
            className="w-100 mt-4 mb-4"
          >
            Cancel
          </Button>
        </ModalBody>
      </Modal>

      <Modal isOpen={addLeaveModal} toggle={toggleAddLeaveModal}>
        <ModalHeader className="pb-0">
          Add Leave Request
          <Button
            className="close"
            onClick={toggleAddLeaveModal}
            style={{ position: 'absolute', top: '10px', right: '10px' }}
          >
            <FaTimes />
          </Button>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmitLeave}>
            <FormGroup>
              <Label>{view.startsWith('staff') ? 'Staff' : 'Student'}</Label>
              <Select
                value={newLeave.userId}
                onChange={(option) => handleNewLeaveChange('userId', option)}
                options={view.startsWith('staff') ? teachers : students}
                placeholder={`Select ${view.startsWith('staff') ? 'Staff' : 'Student'}`}
                isSearchable
              />
            </FormGroup>
            <FormGroup>
              <Label>Leave Type</Label>
              <Select
                value={newLeave.leaveType}
                onChange={(option) => handleNewLeaveChange('leaveType', option)}
                options={leaveTypes}
                placeholder="Select Leave Type"
                isSearchable
              />
            </FormGroup>
            <FormGroup>
              <Label>Reason</Label>
              <Input
                type="text"
                placeholder="Enter leave reason"
                value={newLeave.leaveReason}
                onChange={(e) => handleNewLeaveChange('leaveReason', e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Days</Label>
              <div className="d-flex">
                <DatePicker
                  selected={newLeave.fromDate}
                  onChange={(date) => handleNewLeaveChange('fromDate', date)}
                  selectsStart
                  startDate={newLeave.fromDate}
                  endDate={newLeave.toDate}
                  placeholderText="From Date"
                  className="form-control mr-2"
                />
                <DatePicker
                  selected={newLeave.toDate}
                  onChange={(date) => handleNewLeaveChange('toDate', date)}
                  selectsEnd
                  startDate={newLeave.fromDate}
                  endDate={newLeave.toDate}
                  minDate={newLeave.fromDate}
                  placeholderText="To Date"
                  className="form-control"
                />
              </div>
            </FormGroup>
            <div className="d-flex justify-content-end">
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Submit'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <ViewLeaveRequestModal isOpen={viewModalOpen} toggle={toggleViewModal} leave={currentLeave} />
      <LeaveRequestModal isOpen={isModalOpen} toggle={toggleModal} />
    </>
  );
};

export default LeaveManagement;
